import { toast } from "react-toastify";
import { FeatureCollection } from "geojson";
import { GeoData } from "@rogoag/shared-ts";
import type { Job } from "../../../../../../api/types";
import { GeoJSONOptionsGroup } from "../../../../../../utilities/types";
import { FileDownloader } from "../../../../../../utilities/downloadFile";
import { GeoJSONOptionsKey, GeoJSONFileKey, GroupedGeoJSONOption } from "../../../ImportTable.types";
import { ColumnContext } from "../ColumnContext";
import { GeoJSONFileColumnViewModel, geoJSONKeyToOptionType, GeoJSONDownloadFormat } from "./geoJSONFileColumn.types";

/**
 * Hook that provides the GeoJSONFileColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useGeoJSONFileColumnViewModel(context: ColumnContext): GeoJSONFileColumnViewModel {
	/**
	 * Flatten GeoJSONOptionsGroup[] into GroupedGeoJSONOption[]
	 */
	function getAvailableOptions(job: Job, optionsKey: GeoJSONOptionsKey): GroupedGeoJSONOption[] {
		const opts: GroupedGeoJSONOption[] = [];
		const optionGroups = job.fileOptions?.[optionsKey] || [];
		optionGroups.forEach((group) => {
			if (group.options && Array.isArray(group.options)) {
				group.options.forEach((opt) => {
					opts.push({ group: group.type.label, option: opt });
				});
			}
		});
		return opts;
	}

	/**
	 * Find the matching option for the current selection
	 */
	function getSelectedOption(
		availableOptions: GroupedGeoJSONOption[],
		currentGeoJSON?: FeatureCollection,
	): GroupedGeoJSONOption | null {
		return (
			availableOptions.find((opt) => opt.option.geoJSON === currentGeoJSON) ||
			(currentGeoJSON && availableOptions.length === 1 ? availableOptions[0] : null)
		);
	}

	/**
	 * Group options by their group label for menu display
	 */
	function getGroupedMenuOptions(availableOptions: GroupedGeoJSONOption[]): Record<string, GroupedGeoJSONOption[]> {
		const groups: Record<string, GroupedGeoJSONOption[]> = {};
		availableOptions.forEach((opt) => {
			if (!groups[opt.group]) groups[opt.group] = [];
			groups[opt.group].push(opt);
		});
		return groups;
	}

	/**
	 * Update job with new GeoJSON selection
	 * Uses functional state update to avoid stale closure issues when multiple updates happen quickly
	 */
	function updateJobGeoJSON(jobId: string, geoJSONKey: GeoJSONFileKey, newGeoJSON?: FeatureCollection): void {
		let updatedJobForValidation: Job | undefined;

		context.setJobs((prevJobs) => {
			const jobIndex = prevJobs.findIndex((j) => j.id === jobId);
			if (jobIndex === -1) return prevJobs;

			const oldJob = prevJobs[jobIndex];
			// Create a new job object (immutable update) instead of mutating the original
			const updatedJob: Job = {
				...oldJob,
				files: { ...oldJob.files, [geoJSONKey]: newGeoJSON },
			};

			const newJobs = [...prevJobs];
			newJobs[jobIndex] = updatedJob;

			// Save for validation outside the functional update
			updatedJobForValidation = updatedJob;

			return newJobs;
		});

		// Validate after state update is queued (using setTimeout to ensure state has been applied)
		setTimeout(() => {
			if (updatedJobForValidation) {
				context.validateJobs([updatedJobForValidation], { updateState: true, clearPrevious: false });
			}
		}, 0);
	}

	/**
	 * Update job with uploaded GeoJSON (also adds to fileOptions)
	 * Uses functional state update to avoid stale closure issues when multiple updates happen quickly
	 */
	function updateJobWithUpload(
		jobId: string,
		optionsKey: GeoJSONOptionsKey,
		geoJSONKey: GeoJSONFileKey,
		newGeoJSON: FeatureCollection,
		fileName: string,
	): void {
		let newJobForValidation: Job | undefined;

		context.setJobs((prevJobs) => {
			const jobIndex = prevJobs.findIndex((j) => j.id === jobId);
			if (jobIndex === -1) return prevJobs;
			const oldJob = prevJobs[jobIndex];

			const optionType = geoJSONKeyToOptionType[geoJSONKey];
			const newOption = { name: fileName, geoJSON: newGeoJSON };
			const newOptionsGroup: GeoJSONOptionsGroup = {
				type: { type: optionType, label: "Uploaded" },
				options: [newOption],
			};

			const existingGroups = oldJob.fileOptions?.[optionsKey] || [];
			const filteredGroups = existingGroups.filter((g) => g.type.label !== "Uploaded");

			const newJob: Job = {
				...oldJob,
				files: { ...oldJob.files, [geoJSONKey]: newGeoJSON },
				fileOptions: { ...oldJob.fileOptions, [optionsKey]: [...filteredGroups, newOptionsGroup] },
			};

			const newJobs = [...prevJobs];
			newJobs[jobIndex] = newJob;

			// Save for validation outside the functional update
			newJobForValidation = newJob;

			return newJobs;
		});

		// Validate after state update is queued
		setTimeout(() => {
			if (newJobForValidation) {
				context.validateJobs([newJobForValidation], { updateState: true, clearPrevious: false });
			}
		}, 0);
	}

	/**
	 * Handle file upload
	 */
	async function handleUpload(
		jobId: string,
		optionsKey: GeoJSONOptionsKey,
		geoJSONKey: GeoJSONFileKey,
		file: File,
		setIsLoading: (loading: boolean) => void,
		resetFileInput: () => void,
	): Promise<void> {
		setIsLoading(true);
		try {
			const geoJSON = await GeoData.toGeoJSON(file, "SHP");
			updateJobWithUpload(jobId, optionsKey, geoJSONKey, geoJSON, file.name.replace(/\.zip$/i, ""));
		} catch (error) {
			const message = error instanceof Error ? error.message : "Upload failed";
			toast.error(message);
		} finally {
			setIsLoading(false);
			resetFileInput();
		}
	}

	/**
	 * Handle file download with optional format selection
	 * Defaults to shapefile for backward compatibility
	 */
	async function handleDownload(
		currentGeoJSON: FeatureCollection,
		selectedOption: GroupedGeoJSONOption,
		setIsLoading: (loading: boolean) => void,
		format: GeoJSONDownloadFormat = "shapefile",
	): Promise<void> {
		setIsLoading(true);
		try {
			const filename = selectedOption.option.name;
			let file: File;

			switch (format) {
				case "kml":
					file = await GeoData.toKML_File(currentGeoJSON, "GeoJSON", filename);
					FileDownloader.downloadFile(file, `${filename}.kml`);
					break;
				case "kmz":
					file = await GeoData.toKMZ_File(currentGeoJSON, "GeoJSON", filename);
					FileDownloader.downloadFile(file, `${filename}.kmz`);
					break;
				case "geojson":
					file = await GeoData.toGeoJSON_File(currentGeoJSON, "GeoJSON", filename);
					FileDownloader.downloadFile(file, `${filename}.json`);
					break;
				case "shapefile":
				default:
					file = await GeoData.toSHP_File(currentGeoJSON, "GeoJSON", filename);
					FileDownloader.downloadFile(file, `${filename}.zip`);
					break;
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : "Download failed";
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	}

	/**
	 * Delete file (clears files AND removes from fileOptions)
	 * Uses functional state update to avoid stale closure issues when multiple updates happen quickly
	 */
	function handleDelete(
		jobId: string,
		optionsKey: GeoJSONOptionsKey,
		geoJSONKey: GeoJSONFileKey,
		currentGeoJSON: FeatureCollection,
	): void {
		let newJobForValidation: Job | undefined;

		context.setJobs((prevJobs) => {
			const jobIndex = prevJobs.findIndex((j) => j.id === jobId);
			if (jobIndex === -1) return prevJobs;
			const oldJob = prevJobs[jobIndex];

			// Remove the option from fileOptions
			const existingGroups = oldJob.fileOptions?.[optionsKey] || [];
			const updatedGroups = existingGroups
				.map((group) => ({
					...group,
					options: group.options.filter((opt) => opt.geoJSON !== currentGeoJSON),
				}))
				.filter((group) => group.options.length > 0);

			const newJob: Job = {
				...oldJob,
				files: { ...oldJob.files, [geoJSONKey]: undefined },
				fileOptions: { ...oldJob.fileOptions, [optionsKey]: updatedGroups },
			};

			const newJobs = [...prevJobs];
			newJobs[jobIndex] = newJob;

			// Save for validation outside the functional update
			newJobForValidation = newJob;

			return newJobs;
		});

		// Validate after state update is queued
		setTimeout(() => {
			if (newJobForValidation) {
				context.validateJobs([newJobForValidation], { updateState: true, clearPrevious: false });
			}
		}, 0);
	}

	return {
		getAvailableOptions,
		getSelectedOption,
		getGroupedMenuOptions,
		updateJobGeoJSON,
		updateJobWithUpload,
		handleUpload,
		handleDownload,
		handleDelete,
	};
}
